import { TaxInformation, ZReceipt, InvoiceStatus } from './invoices'
import { User } from './interfaces'
import { Business } from './business'
import { Tag } from './tags'

export enum ReviewStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  DENIED = 'DENIED'
}

export const ReviewStatusOptions = [
  { value: 'ALL', label: 'filters.all' },
  { value: ReviewStatus.PENDING, label: 'filters.pending' },
  { value: ReviewStatus.APPROVED, label: 'filters.approved' },
  { value: ReviewStatus.DENIED, label: 'filters.denied' }
]

export const ReviewStatusTranslator: Record<ReviewStatus, string> = {
  [ReviewStatus.APPROVED]: 'filters.approved',
  [ReviewStatus.DENIED]: 'filters.denied',
  [ReviewStatus.PENDING]: 'filters.pending'
}

export const ReviewStatusColor: Record<ReviewStatus, string> = {
  [ReviewStatus.APPROVED]: 'green',
  [ReviewStatus.DENIED]: 'red',
  [ReviewStatus.PENDING]: 'yellow'
}

export interface ExpenseReview {
  id: number
  expense_id: number
  status: ReviewStatus
  reviewer_id: number
  notes?: string | null
  reviewed_at: Date
  created_at: Date
  updated_at: Date
}

export const CategoryOptions = [
  { value: 'oficina', label: 'categories.oficina' },
  { value: 'marketing', label: 'categories.marketing' },
  { value: 'comida', label: 'categories.comida' },
  { value: 'viajes', label: 'categories.viajes' },
  { value: 'renta', label: 'categories.renta' },
  { value: 'servicio', label: 'categories.servicio' },
  { value: 'transporte', label: 'categories.transporte' },
  { value: 'suscripciones', label: 'categories.suscripciones' },
  { value: 'otros', label: 'categories.otros' }
]

export interface Expense {
  id: number
  user_id: number
  category: string
  note?: string
  /**
   * Expense date stored as UTC timestamp.
   *
   * - **From WhatsApp (with receipt):** The date is extracted via OCR from the ticket and stored
   *   as UTC midnight (e.g. ticket says "05/03" → stored as `2025-03-05T00:00:00Z`).
   *   To display it correctly, use `moment.utc()` to avoid timezone shift.
   *   Prefer using `receipt.data.receipt_date` via `displayExpenseDate()` for these cases.
   *
   * - **From dashboard form (no receipt):** The date is selected by the user in local time
   *   and converted with `localDateToUtc()` before sending (e.g. user picks March 5 at midnight
   *   in Mexico → sent as `2025-03-04T06:00:00Z`). To display it correctly, use `moment()`
   *   which converts UTC back to local time.
   *
   * Use `displayExpenseDate(expense.date, receipt?.data?.receipt_date)` from `@/utils/date`
   * to handle both cases automatically.
   */
  date: Date
  amount: number
  name: string
  business_id?: number
  tag_ids?: number[]
  is_processing?: boolean
  invoice_pdf_url?: string
  invoice_xml_url?: string
  invoice_pdf_path?: string
  invoice_xml_path?: string

  invoice_date?: string
  invoice_uuid?: string
  invoice_total?: number
  invoice_subtotal?: number
  invoice_discount?: number
  invoice_rfc_issuer?: string
  invoice_rfc_receiver?: string
  invoice_payment_method?: string
  invoice_payment_form?: string
  invoice_series_folio?: string
  invoice_seal?: string

  receipt_id?: number
  receipt?: ZReceipt
  image?: string | null
  image_url?: string
  pdf?: string | null
  pdf_url?: string
  review?: ExpenseReview | null
  /** Timestamp of when the expense was created (UTC). Use `utcDateToLocalMoment()` to display. */
  created_at: Date
  updated_at?: Date
  deleted_at?: Date
}

export interface ExpenseStatus {
  receipt?: InvoiceStatus
  expense: Expense
  user: User
  reviewer?: User
  review?: ExpenseReview
  tax_information?: TaxInformation
  business?: Business
  tag?: Tag
  tags?: Tag[]
}

export enum DownloadExpensesBulkType {
  CSV = 'CSV',
  ZIP_PDF_XML = 'ZIP_PDF_XML',
  ZIP_IMAGES_PDF = 'ZIP_IMAGES_PDF',
  BUDGET = 'BUDGET'
}

export enum DownloadExpenseType {
  INVOICE_PDF = 'INVOICE_PDF',
  INVOICE_XML = 'XML',
  IMAGE = 'IMAGE',
  PDF = 'PDF'
}

export enum ExpensesFilterBy {
  DATE = 'DATE',
  CREATED_AT = 'CREATED_AT',
  INVOICE_DATE = 'INVOICE_DATE'
}

export const ExpensesFilterByOptions = [
  {
    label: 'filters.sendDate',
    value: ExpensesFilterBy.CREATED_AT
  },
  {
    label: 'filters.expenseDate',
    value: ExpensesFilterBy.DATE
  },
  {
    label: 'filters.invoiceDate',
    value: ExpensesFilterBy.INVOICE_DATE
  }
]
